/* eslint-disable no-underscore-dangle */
import { metricKeys } from '@pulse/metrics';
import { odpPlaceholderMetric } from './odp/utils';
import invert from 'lodash/invert';
import type { AppFlowMetadataScope } from '@src/hooks/flow/useFlowConfigs';

const { baseUrl, module } = window.conviva;

export const ROOT_ELEMENT_ID = 'app';
export const ROOT_SCROLL_CONTAINER_ELEMENT_ID = 'app-scroll-container';

export const productInitFields = {
  EI: {
    DATASET: 'ExperienceInsights',
    TAB_TITLE_TRENDS: 'Trends',
    DEFAULT_METRIC: metricKeys.Attempts,
    TAB_TITLE_NOC: 'Real-Time',
  },
  AI: {
    DATASET: 'AdExperience',
    TAB_TITLE_TRENDS: 'Ad Experience',
    DEFAULT_METRIC: metricKeys.Plays,
    TAB_TITLE_NOC: 'Real-Time',
  },
  APP: {
    DATASET: 'AppExperience',
    TAB_TITLE_TRENDS: 'Trends',
    DEFAULT_METRIC: metricKeys.AppSubscribers,
    TAB_TITLE_NOC: 'Real-Time',
  },
  ODP: {
    DATASET: 'odp',
    TAB_TITLE_TRENDS: 'Mini ODP',
    DEFAULT_METRIC: odpPlaceholderMetric.name,
    TAB_TITLE_NOC: '',
  },
  API: {
    DATASET: 'api_usage_insights',
    TAB_TITLE_TRENDS: '',
    DEFAULT_METRIC: 'RealtimeAPICalls',
    TAB_TITLE_NOC: '',
  },
  // As each entry point is a module, base needs this definition
  // even though it's not a product
  BASE: {
    DATASET: 'NA',
    TAB_TITLE_TRENDS: 'Pulse',
    DEFAULT_METRIC: 'NA',
    TAB_TITLE_NOC: '',
  },
};

export const PRODUCT_MODE_MAP = {
  EI: 'EI',
  AI: 'AI',
  APP: 'APP',
  ODP: 'ODP',
};

export const CONTEXT_MAP = {
  EI: 'ei',
  AI: 'ai',
  APP: 'app',
  ODP: 'odp',
};

export const INVERT_CONTEXT_MAP = invert(CONTEXT_MAP);

export const CURRENT_PRODUCT_MODE = module;
export const isEiModule = module === PRODUCT_MODE_MAP.EI;
export const isAdModule = module === PRODUCT_MODE_MAP.AI;
export const isAppModule = module === PRODUCT_MODE_MAP.APP;
export const isOdpModule = module === PRODUCT_MODE_MAP.ODP;

export const TAB_TRENDS_KEY = 'trends';
export const TAB_TITLE_AI_ALERT = 'AI Alerts';
export const TAB_TITLE_OVB = 'Overview';
export const TAB_TITLE_AI_ALERTS_COHORTS = 'AI Cohorts';
export const TAB_TITLE_AI_ROADMAP = 'AI Roadmap';
export const VIEWER_MODULE = 'Viewers';

export const { DATASET, TAB_TITLE_TRENDS, TAB_TITLE_NOC, DEFAULT_METRIC } =
  productInitFields[module];

export const NOC_VIEW_PREFIX_TEXT = 'Real-Time: ';
export const NOC_VIEW_FILTERS_TEXT = 'Filters';

export const NO_COMPARE_MODE = 0;
export const COMPARE_MODE_TIME_SERIES = 1;
export const COMPARE_MODE_SAVED_FILTERS = 2;
export const COMPARE_MODE_CUSTOM = 3;

export const FONT_FAMILY = '"Roboto", sans-serif';

export const TOOLTIP_LABEL_FORMAT = 'ddd, MMM DD YYYY HH:mm';
export const TOOLTIP_LABEL_FORMAT_RT = 'ddd, MMM DD YYYY HH:mm:ss';
export const DATE_TIME_FORMAT = 'MMM DD YYYY HH:mm:ss';

export const EVENT_DT_FORMAT = 'HH:mm MMM DD YYYY';
export const SLIDER_DT_FORMAT = 'MMM DD HH:mm';

export const INTERNAL_UNKNOWN = 'CONVIVA_INTERNAL_UNKNOWN';
export const UNKNOWN = 'UNKNOWN';
export const UNKNOWN_NAME = ['Unknown', 'UNKNOWN'];

export const ALL_TRAFFIC = 'All Traffic';
export const LIVE_TRAFFIC = 'Live Traffic';
export const VOD_TRAFFIC = 'VOD Traffic';

export const POPULATION_THRESHOLD_BASELINE = 'Audience Threshold for';

export const MAX_COMPARE_FILTER_BARS = 20;
export const SOFT_MAX_COMPARE_FILTER_BARS = MAX_COMPARE_FILTER_BARS / 2;
export const NO_DIMENSIONS_SELECTED = 0;
export const ALL_DIMENSIONS_SELECTED = 1;
export const SOME_DIMENSIONS_SELECTED = 2;
export const DISABLE_DIMENSION_SELECTION = 3;

export const COMPARE_TIMESERIES_TITLE = 'Time series';
export const COMPARE_SAVED_FILTER_TITLE = 'Saved Filters';

export const MODAL_COMPARE = 1;
export const MODAL_COMPARE_ACTION_SOURCE_FILTER_BREADCRUMB = 1;
export const MODAL_COMPARE_ACTION_SOURCE_TOP_TRENDS_MENU = 2;
export const MODAL_COMPARE_ACTION_SOURCE_METRIC_ORDER_BY_MENU = 3;
export const MODAL_COMPARE_ACTION_SOURCE_DATE_PICKER = 4;
export const MODAL_COMPARE_ACTION_SOURCE_FILTER_POPUP_APPLY = 5;
export const MODAL_COMPARE_ACTION_SOURCE_FILTER_POPUP_RECENT = 6;
export const MODAL_COMPARE_ACTION_SOURCE_FILTER_POPUP_NEW_FILTER_SAVE = 7;
export const MODAL_COMPARE_ACTION_SOURCE_FILTER_POPUP_NEW_FILTER_SAVE_AND_APPLY = 8;
export const MODAL_COMPARE_ACTION_SOURCE_CHART_SELECTION_METRICS_PANEL = 9;
export const MODAL_COMPARE_ACTION_SOURCE_FILTER_PRESET_TOGGLE = 10;

export const ROUTE_EXP_INSIGHTS = 1;
export const ROUTE_AD_INSIGHTS = 2;
export const ROUTE_APP_INSIGHTS = 14;
export const ROUTE_CUSTOM_DASHBOARD = 11;

export const CUSTOM_DASHBOARD_CONTEXT_PATH_COMPARE = 'compare';
export const COMPARE_PATH_TEST_STR = '/compare';

export const HOME_SHORT_PATH = '/';

const HOMES = {
  APP: `${baseUrl}/dashboard/overview`,
};

export const HOME_LONG_PATH = module === 'APP' ? HOMES[module] : `${baseUrl}/dashboard/trends`;
export const HOME_PATHS = [HOME_SHORT_PATH, HOME_LONG_PATH];
export const ALL_METRICS_PATH = `${baseUrl}/dashboard/all-metrics`;
export const CUSTOM_DASHBOARD_PATH = `${baseUrl}/dashboard/*/new`;
export const NOC_HOME_PATH = `${baseUrl}/dashboard/real-time`;

export const DATE_SHORTCUTS = {
  last120Sec: '120 sec',
  last15Mins: 'Live',
  last1Hour: 'Last 1 hour',
  last2Hours: 'Last 2 hours',
  last6Hours: 'Last 6 hours',
  past24Hours: 'Last 24 hours',
  today: 'Today',
  yesterday: 'Yesterday',
  lastWeek: 'Last week',
  sevenDays: '7 days',
  thirtyDays: '30 days',
  thisMonth: 'This month',
  lastMonth: 'Last month',
  past7Days: 'Last 7 days',
  past15Days: 'Last 15 days',
  past30Days: 'Last 30 days',
  past90Days: 'Last 90 days',
  past1Year: 'Last 1 year',
};

export const METRICS_CONTAINER_METRICS_VIEW = {
  DEFAULT_ORDER: 1, // pre-defined order of charts
  LIST: 2, // shows charts in minimized mode
  LEXICAL: 3, // alphabetical order
  SORT_BY_DEVIATION: 4,
  ALL_METRICS: 5, // if charts are removed, this options un-hide all of them
  EXPORT: 6,
  AUDIENCE_METRICS: 7,
};

export const TABS_MAX_LENGTH = 500;

// shouldn't it be an api call
export const DASHBOARD_TYPE = {
  TEMP_TAB: 0,
  COMPARE: 1,
  NOC: 2,
  OVB: 3,
  TRENDS: 5,
  ML: 4,
  ERROR_DIAGNOSTICS: 9,
  TIMELINE: 50,
  ALERT_DIAGNOSTICS: 11,
  NOC_DRILLDOWN: 12,
  APP_NOC_DRILLDOWN: 22,
  VIEWER_MODULE: 51,
  AI_ALERT_DIAGNOSTICS: 13,
  BROKEN_ASSETS_DRILLDOWN: 15,
  NEWAPP_DRILLDOWN: 21,
  APP_VIEWER_MODULE: 52,
  APP_EXPERIENCE_DRILLDOWN: 16,
  APP_EXPERIENCE: 55,
  TRENDS_DRILLDOWN: 25,
  AI_ROADMAP: 7,
  EXPERIENCE_ALERT_DIAGNOSTICS: 17,
  CUSTOM_DASHBOARD_DRILLDOWN: 18,
  APP_CUSTOM_DASHBOARD_DRILLDOWN: 19,
  POLICY_TRACKING: 20,
};

export const DASHBOARD_DISPLAY_NAME = {
  [DASHBOARD_TYPE.TRENDS]: 'Trends',
  [DASHBOARD_TYPE.AI_ALERT_DIAGNOSTICS]: 'AI Alert Diagnostics',
  [DASHBOARD_TYPE.ALERT_DIAGNOSTICS]: 'Manual Alert Diagnostics',
  [DASHBOARD_TYPE.ERROR_DIAGNOSTICS]: 'Error Diagnostics',
  [DASHBOARD_TYPE.COMPARE]: 'Compare Dashboard',
  [DASHBOARD_TYPE.TIMELINE]: 'Viewer Timeline',
  [DASHBOARD_TYPE.NOC]: 'Realtime',
  [DASHBOARD_TYPE.ML]: 'MetricLens',
  [DASHBOARD_TYPE.OVB]: 'Overview Dashboard',
  [DASHBOARD_TYPE.VIEWER_MODULE]: 'Viewer Module',
  [DASHBOARD_TYPE.APP_EXPERIENCE]: 'Trends',
  [DASHBOARD_TYPE.AI_ROADMAP]: 'AI Roadmap',
};

export const DASHBOARD_URL_PATH = {
  [DASHBOARD_TYPE.ERROR_DIAGNOSTICS]: `/dashboard/error-diagnostics/`,
  [DASHBOARD_TYPE.COMPARE]: `/dashboard/compare/`,
  [DASHBOARD_TYPE.TIMELINE]: `/dashboard/timeline/`,
  [DASHBOARD_TYPE.NOC]: `/dashboard/real-time/`,
  [DASHBOARD_TYPE.ML]: `/dashboard/metriclens/`,
  [DASHBOARD_TYPE.OVB]: `/dashboard/overview/`,
  [DASHBOARD_TYPE.VIEWER_MODULE]: `/dashboard/viewer-module/saved/`,
  [DASHBOARD_TYPE.TRENDS]: `/dashboard/trends/saved/`,
  [DASHBOARD_TYPE.APP_EXPERIENCE]: `/dashboard/app-experience/saved/`,
};

export const DASHBOARD_URL = {
  [DASHBOARD_TYPE.ERROR_DIAGNOSTICS]: `${baseUrl}/dashboard/error-diagnostics/`,
  [DASHBOARD_TYPE.COMPARE]: `${baseUrl}/dashboard/compare/`,
  [DASHBOARD_TYPE.TIMELINE]: `${baseUrl}/dashboard/timeline/`,
  [DASHBOARD_TYPE.NOC]: `${baseUrl}/dashboard/real-time/`,
  [DASHBOARD_TYPE.ML]: `${baseUrl}/dashboard/metriclens/`,
  [DASHBOARD_TYPE.OVB]: `${baseUrl}/dashboard/overview/`,
  [DASHBOARD_TYPE.VIEWER_MODULE]: `${baseUrl}/dashboard/viewer-module/saved/`,
  [DASHBOARD_TYPE.TRENDS]: `${baseUrl}/dashboard/trends/saved/`,
  [DASHBOARD_TYPE.APP_EXPERIENCE]: `${baseUrl}/dashboard/app-experience/saved/`,
  [DASHBOARD_TYPE.AI_ROADMAP]: `${baseUrl}/dashboard/ai-roadmap`,
};

export const DASHBOARD_SHARED_URL = {
  [DASHBOARD_TYPE.TRENDS]: `${baseUrl}/dashboard/trends/sharing/`,
  [DASHBOARD_TYPE.AI_ALERT_DIAGNOSTICS]: `${baseUrl}/dashboard/ai-alerts/diagnostics/sharing/`,
  [DASHBOARD_TYPE.ERROR_DIAGNOSTICS]: `${baseUrl}/dashboard/error-diagnostics/sharing/`,
  [DASHBOARD_TYPE.COMPARE]: `${baseUrl}/dashboard/compare/sharing/`,
  [DASHBOARD_TYPE.TIMELINE]: `${baseUrl}/dashboard/timeline/sharing/`,
  [DASHBOARD_TYPE.NOC]: `${baseUrl}/dashboard/real-time/sharing/`,
  [DASHBOARD_TYPE.ML]: `${baseUrl}/dashboard/metriclens/sharing/`,
  [DASHBOARD_TYPE.OVB]: `${baseUrl}/dashboard/overview/sharing/`,
  [DASHBOARD_TYPE.VIEWER_MODULE]: `${baseUrl}/dashboard/viewer-module/`,
  [DASHBOARD_TYPE.APP_EXPERIENCE]: `${baseUrl}/dashboard/app-experience/sharing/`,
};

export const TEMP_DASHBOARD_URL = {
  [DASHBOARD_TYPE.ERROR_DIAGNOSTICS]: `${baseUrl}/dashboard/error-diagnostics/temp/`,
  [DASHBOARD_TYPE.COMPARE]: `${baseUrl}/dashboard/compare/`,
  [DASHBOARD_TYPE.NOC]: `${baseUrl}/dashboard/real-time/`,
  [DASHBOARD_TYPE.ML]: `${baseUrl}/dashboard/metriclens/`,
  [DASHBOARD_TYPE.TIMELINE]: `${baseUrl}/dashboard/timeline/temp/`,
  [DASHBOARD_TYPE.ALERT_DIAGNOSTICS]: `${baseUrl}/alert/diagnostics/`,
  [DASHBOARD_TYPE.NOC_DRILLDOWN]: `${baseUrl}/dashboard/trends/rt-drilldown/`,
  [DASHBOARD_TYPE.APP_NOC_DRILLDOWN]: `${baseUrl}/dashboard/app-experience/rt-drilldown/`,
  [DASHBOARD_TYPE.VIEWER_MODULE]: `${baseUrl}/dashboard/viewer-module/`,
  [DASHBOARD_TYPE.AI_ALERT_DIAGNOSTICS]: `${baseUrl}/dashboard/ai-alerts/diagnostics/`,
  [DASHBOARD_TYPE.BROKEN_ASSETS_DRILLDOWN]: `${baseUrl}/dashboard/trends/ba-drilldown/`,
  [DASHBOARD_TYPE.NEWAPP_DRILLDOWN]: `${baseUrl}/dashboard/trends/na-drilldown/`,
  [DASHBOARD_TYPE.APP_EXPERIENCE_DRILLDOWN]: `${baseUrl}/dashboard/app-experience/drilldown/`,
  [DASHBOARD_TYPE.TEMP_TAB]: `${baseUrl}/dashboard/temp/`,
  [DASHBOARD_TYPE.APP_VIEWER_MODULE]: `${baseUrl}/dashboard/user-timeline/`,
  [DASHBOARD_TYPE.TRENDS_DRILLDOWN]: `${baseUrl}/dashboard/trends/drilldown/`,
  [DASHBOARD_TYPE.EXPERIENCE_ALERT_DIAGNOSTICS]: `${baseUrl}/dashboard/ai-alerts/diagnostics/`,
  [DASHBOARD_TYPE.CUSTOM_DASHBOARD_DRILLDOWN]: `${baseUrl}/dashboard/trends/cd-drilldown/`,
  [DASHBOARD_TYPE.APP_CUSTOM_DASHBOARD_DRILLDOWN]: `${baseUrl}/dashboard/app-experience/cd-drilldown/`,
};

export const PRESET_DASHBOARD_ROUTE_PATH = `${baseUrl}/dashboard/preset/:id` as const;

export const FLOW_PRESET_DASHBOARD_ROUTE_PATH = `${baseUrl}/dashboard/preset/flow/:id` as const;
export const FRUSTRATION_SIGNAL_PRESET_DASHBOARD_ROUTE_PATH =
  `${baseUrl}/dashboard/preset/frustration-signal/:id` as const;
export const EVENT_PRESET_DASHBOARD_ROUTE_PATH = `${baseUrl}/dashboard/preset/event/:id` as const;

export const SAVED_DASHBOARD_PATHS = {
  COMPARE: 'compare',
  compare: 'compare',
  ERROR_DIAGNOSTICS: 'error-diagnostics',
  error: 'error-diagnostics',
  TRENDS: 'trends/saved',
  trends: 'trends',
  NOC: 'real-time',
  ML: 'metriclens',
  TIMELINE: 'timeline',
  OVB: 'overview',
  VM: 'viewer-module/saved',
  App_Experience: 'app-experience/saved',
};

export const TEMP_DASHBOARD_PATHS = {
  [DASHBOARD_TYPE.TRENDS_DRILLDOWN]: 'trends/drilldown/',
};

export const PAGE_TYPE = {
  SHARING: 1,
  TEMP_DASHBOARD: 2,
  SAVED_DASHBOARD: 3,
};

export const USER_ROLE_READONLY = 'Read Only';

export const DEFAULT_SLIDER_CHART_TICKS = 12;
export const MAX_SLIDER_CHART_TICKS = 30;

export const PRECISION_POLICY_PATH = `/app/experience-insights/precision/precision_policy`;
export const PRECISION_RESILIENCY_PATH = `/app/precision/resiliency`;
export const MBR_POLICY_PATH = `/app/precision/mbr_policy`;
export const CHANGE_LOG_PATH = `/app/experience-insights/change_log`;

export const GRAND_FILTER_FILTER_ALERT_MSG = `As part of your Pulse 5 packaging,
new tag limits apply to the tags used with both Pulse 4 and Pulse 5 filters.
While filters are shared across Pulse 4 and Pulse 5, the available tags and tag
limits will be defined in your Pulse 5 package. Reach out to your Conviva
representative for more details.`;

export const AND_DELIMITER = '__AND__';
export const OR_DELIMITER = '__OR__';

export const EXPORT_TRENDS = 'trends-dashboard';
export const EXPORT_ERROR_DIAGNOSTICS = 'error-dashboard';
export const EXPORT_COMPARE_DASHBOARD = 'compare-dashboard';
export const EXPORT_WIDGET_TRENDS_ALL_METRICS = 'trends-widget-all-metrics';
export const EXPORT_WIDGET_TRENDS_AUDIENCE_METRICS = 'trends-widget-audience-metrics';
export const EXPORT_WIDGET_TRENDS_METRIC = 'trends-widget-metric'; // time series
export const EXPORT_WIDGET_TRENDS_DIAGNOSTICS = 'trends-widget-diagnostics';
export const EXPORT_WIDGET_TRENDS_DISTRIBUTION = 'trends-widget-distribution';
export const EXPORT_TRENDS_VIEWER_DRILLDOWN_DISTRIBUTION = 'trends-widget-vd-distribution';
export const EXPORT_WIDGET_TRENDS_DIMENSIONS = 'trends-widget-dimensions';
export const EXPORT_WIDGET_TRENDS_SESSIONS = 'trends-widget-sessions';
export const EXPORT_WIDGET_COMPARE = 'compare-widget';
export const EXPORT_WIDGET_ERROR_BIRD_CHART = 'error-widget-bird-chart';
export const EXPORT_WIDGET_ERROR_BAR_CHART = 'error-widget-bar-chart';
export const EXPORT_WIDGET_ERROR_TOP_TRENDS_ERROR_TABLE = 'error-widget-trends-error-table';
export const EXPORT_WIDGET_ERROR_SESSIONS_ERROR_TABLE = 'error-widget-sessions-error-table';
export const EXPORT_REALTIME = 'realtime-dashboard';
export const EXPORT_ML = 'metriclens-dashboard';
export const EXPORT_TIMELINE = 'timeline';
export const EXPORT_VM = 'viewer-module';

export const SET_DEFAULT_LANDING_PAGE = 'default-landing-page';

export const MAX_RETRIES = 5;
export const CDN_ACCOUNT_TYPE = 15;

export const ORDER_SEPARATOR_STRING = '%%';

export const FILTERS_CACHE_EXPIRY = 10; // in minutes
export const MAX_FILTER_OPTIONS = 25;

// NOC and Thresholds
export const NOC_MODE_TRENDS = 'trends';
export const NOC_MODE_FILTERS = 'filters';

export const TAB_TITLE_METRIC_LENS = 'MetricLens';

// CDN is permanently excluded
export const NOC_EXCLUDE_DIMENSIONS = ['CDN'];

export const NOC_REFRESH_INTERVAL = 20 * 1000;

export const RT_ON_TRENDS_REFRESH_INTERVAL = 10; // in secs

//keeping in sync with VM live on P4 which has 8 sec refresh
export const VM_LIVE_REFRESH_INTERVAL = 8 * 1000;

export const DEFAULT_SORT = {
  key: 'ConcurrentPlays',
  order: 'desc',
};

export const ERROR_LOGIC_MODE_CONFIG = 'config';
export const ERROR_LOGIC_MODE_LOG = 'log';
export const DEFAULT_CONFIG_SORT = {
  key: 'updatedAt',
  order: 'desc',
};

export const ERR_CONFIG_DATE_FORMAT = 'MMM DD YYYY HH:mm:ss';

export const ADMIN_ROLE_NAME = 'Admin';
export const STAFF_ROLE_NAME = 'Staff';
export const CS_ROLE_NAME = 'Customer Service';

export const ADMIN_ROLE_ID = 1;

// Nexa Permission levels
export const NEXA_PERMISSION = {
  NONE: null, // No access
  NO: 0, // No access
  USAGE: 1, // Usage only access
  ADMIN: 2, // Usage + Enable Nexa access
} as const;

// API Management Permission levels
export const API_MANAGEMENT_PERMISSION = {
  NONE: null, // Hide — no access
  VIEW: 1, // View list only, no client secrets
  EDIT: 2, // Full CRUD access including client secrets
} as const;

export const NOC_DRILLDOWN = 'noc-drilldown';
export const BROKEN_ASSETS_DRILLDOWN = 'broken-assets-drilldown';
export const NEWAPP_DRILLDOWN = 'newapp-drilldown';
export const TRENDS_DRILLDOWN = 'trends-drilldown';

export const AD_PLAYS = metricKeys.AdPlays;
export const AD_ID = 'c3.ad.id';
export const AD_CREATIVE_ID = 'c3.ad.creativeId';
export const FIRST_AD_ID = 'c3.ad.firstAdId';
export const FIRST_AD_CREATIVE_ID = 'c3.ad.firstCreativeId';

export const GOOGLE_AD_MANAGER = 'GDFP';

export const MAX_CHART_METRICS = 999; // removed limit

// metric vaues < limit will be marked as low if feature is on
export const LOW_VALUE_STR = 'low';

// ended plays metrics will be masked for CDN dimension
export const INVALID_VALUE_STR = 'NA';

export const OVB_MODE_EXPERIENCE = 'ovb';
export const OVB_MODE_CONFIG = 'ovb-config';

export const OVB_SPI_WIDGET_NAME = 'Streaming Performance Index';
export const OVB_OPPORTUNITIES_WIDGET_NAME = 'Improvement Opportunities';
export const OVB_OPPORTUNITIES_STARTUP_FAIL = 'Streams that failed to start';
export const OVB_OPPORTUNITIES_PLAYBACK_ISSUES = 'Streams that played with issues';

export const SERVICE_INTEGRATION_MODE_MAIN = 'main';
export const SERVICE_INTEGRATION_MODE_FORM = 'form';

export const SERVICE_INT_DATE_FORMAT = 'MMM DD YYYY HH:mm';
export const DEFAULT_AVL_INT_SORT = {
  key: 'updatedAt',
  order: 'desc',
};

export const METRIC_POPUP_TITLE = 'Select metrics';
export const FLOW_METRIC_POPUP_TITLE = 'Select metrics and flows';
export const METRIC_POPUP_OK_TEXT = 'Apply';
export const METRIC_NEWAPP_VERSION = 'm3.dv.n';
export const TOOLTIP_LABEL_SPI_STREAMS = 'SPI Streams';
export const TOOLTIP_LABEL_IMPACTED_UNIQUE = 'Impacted Unique Devices';
export const OPEN_TRENDS = 'Open in Trends';
export const LATEST_RELEASE = 'Latest Release';
export const OLDER_RELEASE = 'Older Release';
// This global account is harcoded for some borderline cases in PII request
export const DEFAULT_GLOBAL_ACCOUNT = 99;

export const SAVE_SERVICE_DASHBOARD_KEYS = {
  TRENDS: 'trends',
  NOC: 'realtime',
  APP_EXPERIENCE: 'app-experience',
};

export const SAVED_DASHBOARDS_BASE_KEY = 'saved-dashboards';
export const SAVED_DASHBOARDS_STATE_KEY = 'save-service';

export const MARIO = 'mario';

export const LOCAL_SUFFIX = 'Local';
export const PERCENTAGE_SUFFIX = 'Percentage';
export const SESSION_TAGS_TITLE = 'Session Tags';

export const HEAVY_METRICS_MESSAGE =
  'Unique Devices based metric queries for >30days can take a long time to load.';
export const TEMPLATE_METRIC_PROCESSING_MESSAGE = 'Please wait, processing metric(s).';
export const USER_DEFINED_METRIC_CREATING_MESSAGE = 'Please wait, creating metric(s).';

export const INTERNAL_UNKNOWN_TOOLTIP = 'When the underlying value is NULL, empty.';
export const UNKNOWN_TOOLTIP = `When we don't find a mapping in portal for dimensions like GEO, Device etc.`;
export const LUIGI_DELETED_STATUS = 40001;

// Dashboard data scope
export const DASHBOARD_DATA_SCOPE = {
  EVENT: 'event',
  SESSION: 'session',
};

// Delaring them to be used in place of "magic numbers"
export const ZERO = 0;
export const HUNDRED = 100;
export const MS_TO_SEC_SCALE = 1000;
export const CONVIVA_LEARNING_CENTER_CUSTOMER_ID = 1960185607;

export const RUNNING = 'running';
export const AVAILABLE = 'available';
export const METRIC_TYPE_MAP = {
  FLOW: 'flow',
  METRIC: 'metric',
};

export const USECASE_KEY_WIDGET_DRILLDOWN = 'widget-drilldown';

export enum QueryViewId {
  USER_TIMELINE = 'user-timeline',
  USERS = 'users',
  NETWORK_REQUEST = 'network-request',
  FLOW_NETWORK_REQUEST = 'flow-network-request',
  ERROR_ANALYSIS = 'error-analysis',
  TIMESERIES = 'timeseries',
  TIMESERIES_WITH_METRIC_FILTER = 'timeseries-with-metric-filter',
  HIGH_CARDINALITY_SAMPLED_DATA = 'high-cardinality-sampled-data',
}

export const APP_FLOW_SCOPE_VIEW_ID_MAP: Record<AppFlowMetadataScope, QueryViewId | undefined> = {
  error: QueryViewId.ERROR_ANALYSIS,
  service: QueryViewId.FLOW_NETWORK_REQUEST,
  flow: undefined,
  global: undefined,
};

export const APP_FLOW_SPECIAL_TOOLTIPS_ERROR_METRIC_TYPES = [
  'errors_in_abandoned_flows',
  'errors_in_completed_flows',
];

export const DIMENSION_MAP = {
  BROWSER: 'm3.dv.br',
  BROWSER_VERSION: 'm3.dv.brv',
  DEVICE_MODEL: 'm3.dv.mod',
  DEVICE_TYPE: 'm3.dv.hwt',
  FRAMEWORK: 'm3.dv.fw',
  FRAMEWORK_VERSION: 'm3.dv.fwv',
  LIVE_VIDEO: 'c3.video.isLive',
  OS: 'm3.dv.os',
  OS_VERSION: 'm3.dv.osv',
  PLAYER_NAME: 'c3.player.name',
};
